/**
 * Which auth flow the SPA uses for the current build environment.
 *
 * SINGLE SOURCE OF TRUTH: the presence of `REACT_APP_AUTH_HOST` in the
 * build environment decides auth mode. `simpl-api/deploy-frontend.sh`
 * sets that variable in its per-env `case`, so the shell script is the
 * one place a new env needs to be listed. Any env whose case sets
 * `REACT_APP_AUTH_HOST` to a real simpl_auth URL runs in `simpl_auth`
 * mode; the fallthrough case leaves it pointing at auth-staging (also
 * simpl_auth). Old envs that had no simpl_auth deploy of their own
 * ran in `cognito` mode via a hardcoded env-list — that list is gone.
 *
 * If a NEW env is added to `deploy-frontend.sh`'s case, no code edit is
 * needed here or in `simplAuthSession.ts`. This replaces the three
 * parallel env-lists (`SIMPL_AUTH_ENVS` here, `ENVS_WITH_OWN_AUTH_STACK`
 * and `AUTH_STACK_PIGGYBACK` in `simplAuthSession.ts`) — one of them was
 * missed during the 2026-09-18 rbac rename and the SPA rendered a
 * Cognito form instead of redirecting. See
 * plans/active/2026-09-18-rename-stg-factory-env-to-rbac.md § "Eighth
 * gap: SPA authMode env-list missed".
 *
 * Safety:
 *   - `prod`/`production` is ALWAYS `simpl_auth` — hard-coded first, so
 *     no env var or misconfiguration can put prod into cognito mode.
 *   - `REACT_APP_AUTH_MODE` may override for NON-prod envs only (escape
 *     hatch for one-off review apps / local testing).
 */
export type AuthMode = "simpl_auth" | "cognito";

export function getAuthMode(): AuthMode {
  const env = process.env.REACT_APP_ENV?.trim().toLowerCase() || "local";

  // Hard safety: prod can never be anything but simpl_auth.
  if (env === "prod" || env === "production") return "simpl_auth";

  // Non-prod explicit override (escape hatch for review apps + local testing).
  const override = process.env.REACT_APP_AUTH_MODE?.trim().toLowerCase();
  if (override === "cognito" || override === "simpl_auth") {
    return override;
  }

  // Local dev proxies to a simpl_auth container via the dev-proxy on :8088.
  if (env === "local" || env === "") return "simpl_auth";

  // Every deployed env now points at a real simpl_auth. `deploy-frontend.sh`
  // sets REACT_APP_AUTH_HOST from its per-env case; if that variable made
  // it into the build with a non-empty value, we're in simpl_auth mode.
  // The one variable is set by ONE case statement, so a new env can never
  // silently be in a different mode from what its deploy-frontend.sh line
  // implies.
  const authHost = process.env.REACT_APP_AUTH_HOST?.trim();
  return authHost ? "simpl_auth" : "cognito";
}

export const isCognitoAuthMode = (): boolean => getAuthMode() === "cognito";
